import {Component, TemplateRef, ViewChild} from "@angular/core";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {LocalizationsProvider} from "../../i18n/localizations-provider";
import {LocalizationStrings} from "../../i18n/i18n-model";
import {TranslatedString, Translation, TranslationService} from "./translation.service";
import {Notifications, NotificationType} from "../../tools/notifications/notifications";
import {SupportedLanguagesProvider} from "../../tools/supported-languages-provider";
import {LanguageDefinition} from "../../tools/language-definition";
import {SelectOption} from "../../tools/select/select.component";
import {buildNewLanguageOptions} from "./new-language-options";
import {languageFlag} from "../../tools/language-flag";
import {getFirstSelectedFile} from "../../tools/form/get-first-selected.file";
import {TranslationFileReaderService} from "./translation-file-reader.service";
import {DownloaderService} from "../../tools/downloader.service";
import {finalize} from "rxjs/operators";

const ENGLISH_LANGUAGE_CODE = "en-gb";

@Component({
    selector: "app-translations",
    templateUrl: "./translations.component.html",
    standalone: false
})
export class TranslationsComponent {

  readonly groups: TranslationGroup[];
  readonly supportedLanguages: LanguageDefinition[];
  private readonly savedTranslations: Map<string, Record<string, string>>;
  private readonly englishLocalizations: LocalizationStrings;

  @ViewChild("addLanguageModal") private readonly addLanguageModal!: TemplateRef<unknown>;
  private modalRef: BsModalRef | undefined;
  readonly newLanguageOptions: SelectOption[] = buildNewLanguageOptions();
  selectedNewLanguage: string | undefined;
  addingTranslation = false;

  constructor(
    localizationsProvider: LocalizationsProvider,
    private readonly translationService: TranslationService,
    private readonly notifications: Notifications,
    supportedLanguagesProvider: SupportedLanguagesProvider,
    private readonly modalService: BsModalService,
    private readonly downloaderService: DownloaderService,
    private readonly translationFileReaderService: TranslationFileReaderService
  ) {
    const localizations = localizationsProvider.getLocalizations();
    this.supportedLanguages = supportedLanguagesProvider.getLanguages();
    this.groups = buildTranslationGroups(localizations, this.supportedLanguages);
    this.savedTranslations = copySavedTranslations(localizations);
    this.englishLocalizations = localizations.get(ENGLISH_LANGUAGE_CODE)!;
  }

  saveTranslations() {
    const changedTranslations = this.findChangedTranslations();

    if (changedTranslations.length > 0) {
      this.translationService.saveTranslations(changedTranslations).subscribe({
        next: () => {
          changedTranslations.forEach(translation =>
            this.savedTranslations.get(translation.language)![translation.key] = translation.translation
          );
          this.notifications.addNotification(NotificationType.OK, "translationsSaveSuccessMessage");
        },
        error: () => this.notifications.addNotification(NotificationType.ERROR, "translationsSaveFailureMessage")
      });
    }
  }

  openAddLanguageModal() {
    this.modalRef = this.modalService.show(this.addLanguageModal);
  }

  closeAddLanguageModal() {
    this.modalRef?.hide();
  }

  handleNewLanguageChange(languageCode: string) {
    this.selectedNewLanguage = languageCode;
  }

  addNewLanguage() {
    this.downloaderService.download(
      JSON.stringify({
        language: this.selectedNewLanguage!,
        uiTranslations: this.englishUiTranslations(),
      }),
      `remotest-translations-${this.selectedNewLanguage!}.json`
    );
    this.closeAddLanguageModal();
  }

  importTranslation(file: File) {
    this.translationFileReaderService.readFile(file).subscribe(contents => {
      const translationFile: TranslationFile = JSON.parse(contents);
      this.addingTranslation = true;
      this.translationService.addTranslation(
        translationFile.language,
        languageFlag(translationFile.language),
        translationFile.uiTranslations,
      )
        .pipe(finalize(() => this.addingTranslation = false))
        .subscribe({
          next: () => this.notifications.addNotification(NotificationType.OK, "addTranslationSuccessMessage"),
          error: () => this.notifications.addNotification(NotificationType.ERROR, "addTranslationFailureMessage")
        });
    });
  }

  protected readonly getFirstSelectedFile = getFirstSelectedFile;

  private englishUiTranslations(): TranslationEntry[] {
    return Object.entries(this.englishLocalizations)
      .map(([key, value]) => ({key: key, value: value}));
  }

  private findChangedTranslations(): TranslatedString[] {
    const changedTranslations: TranslatedString[] = [];

    this.groups.forEach(group => {
      const key = group.key as keyof LocalizationStrings;

      this.supportedLanguages.forEach(language => {
        if (group.translations[language.code] !== this.savedTranslations.get(language.code)![key]) {
          changedTranslations.push({language: language.code, key: key, translation: group.translations[language.code]});
        }
      });
    });

    return changedTranslations;
  }
}

function copySavedTranslations(localizations: Map<string, LocalizationStrings>): Map<string, Record<string, string>> {
  const savedTranslations = new Map<string, Record<string, string>>();
  localizations.forEach((strings, language) => savedTranslations.set(language, {...strings}));
  return savedTranslations;
}

function buildTranslationGroups(
  localizations: Map<string, LocalizationStrings>,
  supportedLanguages: LanguageDefinition[]
): TranslationGroup[] {
  const keySource = supportedLanguages
    .map(language => localizations.get(language.code))
    .find(strings => strings !== undefined);

  if (keySource === undefined) {
    return [];
  }

  return (Object.keys(keySource) as (keyof LocalizationStrings)[])
    .map(key => ({
      key: key,
      translations: Object.fromEntries(
        supportedLanguages.map(language => [language.code, localizations.get(language.code)?.[key] ?? ""])
      ),
    }));
}

export interface TranslationGroup {
  readonly key: string;
  translations: Record<string, string>;
}

interface TranslationEntry {
  readonly key: string;
  readonly value: string;
}

interface TranslationFile {
  readonly language: string;
  readonly uiTranslations: Translation[];
}
